import { ResourceBaseService } from "@/core/common/resource-base/resource-base.service";
import { ApiHttpClient } from "@/core/common/http/api-http-client";
import { EmployeeDocumentCategoryDto } from "@/core/features/employee-document-categories/employee-document-category.dto";
import { CreateEmployeeDocumentCategoryDto } from "@/core/features/employee-document-categories/create-employee-document-category.dto";
import { UpdateEmployeeDocumentCategoryDto } from "@/core/features/employee-document-categories/update-employee-document-category.dto";
import { OptionType } from "@/types/OptionType";
import { IRequest } from "@/interfaces/IRequest";
import { applyOptionLoaderPagination } from "@/core/common/presenters/optionLoader";
import { plainToInstance } from "class-transformer";
import { HttpRequest } from "@/core/common/http/http-request";
import { AssignRoleToEmployeeDocumentCategoriesDto } from "@/core/features/employee-document-categories/assign-role-to-employee-document-categories.dto";
import { SignaturePlacementDto } from "@/core/features/employee-document-categories/signature-placement.dto";
import { UpsertSignaturePlacementsDto } from "@/core/features/employee-document-categories/upsert-signature-placements.dto";

export class EmployeeDocumentCategoriesResource extends ResourceBaseService<
  EmployeeDocumentCategoryDto,
  CreateEmployeeDocumentCategoryDto,
  UpdateEmployeeDocumentCategoryDto
> {
  protected EntityDto = EmployeeDocumentCategoryDto;
  protected CreateEntityDto = CreateEmployeeDocumentCategoryDto;
  protected UpdateEntityDto = UpdateEmployeeDocumentCategoryDto;
  protected AssignRoleDto = AssignRoleToEmployeeDocumentCategoriesDto;
  protected UpsertPlacementsDto = UpsertSignaturePlacementsDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/employee-document-categories");
  }

  async optionLoader(inputValue: string, forDocumentation = false): Promise<OptionType[]> {
    if (forDocumentation) {
      const categories = await this.listForDocumentation();
      return categories
        .filter((category) => category.name.toLowerCase().includes(inputValue.toLowerCase()))
        .map((category) => category.mapEntityToOptionType([category.name]));
    }

    const request: IRequest = inputValue ? { search: inputValue, searchBy: ["id", "name", "key"] } : {};
    request.sortBy = "name";
    const { data } = await this.findAll(applyOptionLoaderPagination(request), false);
    return data.map((value) => value.mapEntityToOptionType([value.name]));
  }

  async listForDocumentation(): Promise<EmployeeDocumentCategoryDto[]> {
    const categories = await this.apiHttpClient.request<null, EmployeeDocumentCategoryDto[]>(
      HttpRequest.create({
        url: this.resourceUrl + "/documentation/list",
        auth: true,
      })
    );

    return plainToInstance(EmployeeDocumentCategoryDto, categories);
  }

  async findOneForDocumentation(id: string): Promise<EmployeeDocumentCategoryDto> {
    const category = await this.apiHttpClient.request<null, EmployeeDocumentCategoryDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/documentation/list/" + id,
        auth: true,
      })
    );

    return plainToInstance(EmployeeDocumentCategoryDto, category);
  }

  async assignRole(body: AssignRoleToEmployeeDocumentCategoriesDto): Promise<void> {
    await this.apiHttpClient.request<AssignRoleToEmployeeDocumentCategoriesDto, void>(
      HttpRequest.create({
        url: `${this.resourceUrl}/assign-role`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(this.AssignRoleDto, body, { excludeExtraneousValues: true }),
      })
    );
  }

  async upsertTemplateFile(categoryId: string, file: File): Promise<EmployeeDocumentCategoryDto> {
    const formData = new FormData();
    formData.append("file", file);

    const category = await this.apiHttpClient.request<FormData, EmployeeDocumentCategoryDto>(
      HttpRequest.create({
        url: `${this.resourceUrl}/${categoryId}/template`,
        method: "POST",
        type: "form-data",
        auth: true,
        body: formData,
      })
    );

    return plainToInstance(EmployeeDocumentCategoryDto, category);
  }

  async activateTemplate(categoryId: string): Promise<EmployeeDocumentCategoryDto> {
    const category = await this.apiHttpClient.request<null, EmployeeDocumentCategoryDto>(
      HttpRequest.create({
        url: `${this.resourceUrl}/${categoryId}/template/activate`,
        method: "PATCH",
        auth: true,
      })
    );

    return plainToInstance(EmployeeDocumentCategoryDto, category);
  }

  async deactivateTemplate(categoryId: string): Promise<EmployeeDocumentCategoryDto> {
    const category = await this.apiHttpClient.request<null, EmployeeDocumentCategoryDto>(
      HttpRequest.create({
        url: `${this.resourceUrl}/${categoryId}/template/deactivate`,
        method: "PATCH",
        auth: true,
      })
    );

    return plainToInstance(EmployeeDocumentCategoryDto, category);
  }

  async removeTemplate(categoryId: string): Promise<EmployeeDocumentCategoryDto> {
    const category = await this.apiHttpClient.request<null, EmployeeDocumentCategoryDto>(
      HttpRequest.create({
        url: `${this.resourceUrl}/${categoryId}/template`,
        method: "DELETE",
        auth: true,
      })
    );

    return plainToInstance(EmployeeDocumentCategoryDto, category);
  }

  async getTemplateVersionDownloadUrl(versionId: string): Promise<string> {
    const response = await this.apiHttpClient.request<null, { url: string }>(
      HttpRequest.create({
        url: `${this.resourceUrl}/template-versions/${versionId}/download-url`,
        auth: true,
      })
    );

    return response.url;
  }

  async getTemplateVersionPlacements(versionId: string): Promise<SignaturePlacementDto[]> {
    const placements = await this.apiHttpClient.request<null, SignaturePlacementDto[]>(
      HttpRequest.create({
        url: `${this.resourceUrl}/template-versions/${versionId}/placements`,
        auth: true,
      })
    );

    return plainToInstance(SignaturePlacementDto, placements);
  }

  async saveTemplateVersionPlacements(
    versionId: string,
    body: UpsertSignaturePlacementsDto
  ): Promise<SignaturePlacementDto[]> {
    const placements = await this.apiHttpClient.request<UpsertSignaturePlacementsDto, SignaturePlacementDto[]>(
      HttpRequest.create({
        url: `${this.resourceUrl}/template-versions/${versionId}/placements`,
        method: "PUT",
        auth: true,
        body: plainToInstance(this.UpsertPlacementsDto, body, { excludeExtraneousValues: true }),
      })
    );

    return plainToInstance(SignaturePlacementDto, placements);
  }
}
